import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAction } from 'convex/react';
import { api } from './convex/_generated/api';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Loader2,
  ArrowRight,
  Copy,
  CheckCheck,
  Mail,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function MFASetupaitavel() {
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const userId = location.state?.userId;
  const email = location.state?.email;

  const hasRun = useRef(false);

  const sendOTP = useAction(api.mfa.sendOTP);

  useEffect(() => {
    if (hasRun.current) return;

    hasRun.current = true;

    const setup = async () => {
      try {

        await sendOTP({ userId, email });

        toast.success(`Code sent to ${email}`);
      } catch (err) {
        if (err.message === 'MFA already enabled') {
          navigate('/mfa-verify', {
            state: { userId, email },
          });
        } else {
          toast.error(err.message || 'Failed to set up MFA');
        }
      } finally {
        setLoading(false);
      }
    };

    setup();
  }, []);

  const qrUrl = secret
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=otpauth://totp/MyApp:${userId}?secret=${secret}%26issuer=MyApp`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(secret);

    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={styles.page}>
      <div style={styles.glow1} />
      <div style={styles.glow2} />

      <div style={styles.grid} />

      <div style={styles.card}>
        {/* TOP ICON */}
        <div style={styles.iconWrap}>
          <Mail size={34} color="#fff" />
        </div>

        {/* TITLE */}
        <h1 style={styles.title}>Verify Your Identity</h1>

        <p style={styles.subtitle}>
          We sent a secure verification code to your email address.
        </p>

        {/* EMAIL */}
        <div style={styles.emailBox}>
          <Mail size={16} color="#A5B4FC" />
          <span>{email}</span>
        </div>

        {/* OPTIONAL QR */}
        {qrUrl && (
          <div style={styles.qrWrap}>
            <img src={qrUrl} alt="QR Code" style={styles.qrImage} />
          </div>
        )}

        {/* LOADING / BUTTON */}
        {loading ? (
          <div style={styles.loaderWrap}>
            <Loader2 size={34} style={styles.spin} />

            <p style={styles.loadingText}>
              Sending verification code...
            </p>
          </div>
        ) : (
          <>
            <button
              onClick={() =>
                navigate('/mfa-verify', {
                  state: { userId, email },
                })
              }
              style={styles.btn}
            >
              Continue
              <ArrowRight size={18} />
            </button>

            <p style={styles.footerText}>
              Didn&apos;t receive the email? Check your spam folder.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    width: '100%',
    background: '#050816',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
  },

  grid: {
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
    backgroundSize: '40px 40px',
    maskImage:
      'radial-gradient(circle at center, black 30%, transparent 90%)',
  },

  glow1: {
    position: 'absolute',
    top: -180,
    right: -140,
    width: 420,
    height: 420,
    background: 'rgba(99,102,241,0.28)',
    borderRadius: '50%',
    filter: 'blur(120px)',
  },

  glow2: {
    position: 'absolute',
    bottom: -180,
    left: -140,
    width: 420,
    height: 420,
    background: 'rgba(168,85,247,0.18)',
    borderRadius: '50%',
    filter: 'blur(120px)',
  },

  card: {
    width: '100%',
    maxWidth: 470,
    background: 'rgba(15,23,42,0.75)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 32,
    padding: '48px 40px',
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    position: 'relative',
    zIndex: 10,
    textAlign: 'center',
    boxShadow:
      '0 25px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
    overflow: 'hidden',
  },

  iconWrap: {
    width: 86,
    height: 86,
    borderRadius: 28,
    margin: '0 auto 28px',
    background:
      'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 20px 50px rgba(99,102,241,0.35)',
  },

  title: {
    fontSize: 38,
    fontWeight: 800,
    color: '#fff',
    margin: '0 0 14px',
    letterSpacing: '-0.04em',
    lineHeight: 1.1,
  },

  subtitle: {
    fontSize: 15,
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.62)',
    margin: '0 auto 28px',
    maxWidth: 340,
  },

  emailBox: {
    width: '100%',
    minHeight: 58,
    borderRadius: 18,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 28,
    padding: '0 18px',
    boxSizing: 'border-box',
    backdropFilter: 'blur(12px)',
  },

  qrWrap: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 32,
  },

  qrImage: {
    width: 180,
    height: 180,
    borderRadius: 24,
    padding: 14,
    background: '#fff',
    boxShadow: '0 16px 45px rgba(0,0,0,0.35)',
  },

  loaderWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 18,
    padding: '14px 0',
  },

  loadingText: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.55)',
  },

  btn: {
    width: '100%',
    height: 60,
    border: 'none',
    borderRadius: 18,
    background:
      'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'all 0.25s ease',
    boxShadow: '0 18px 45px rgba(99,102,241,0.35)',
  },

  footerText: {
    marginTop: 18,
    fontSize: 13,
    color: 'rgba(255,255,255,0.42)',
    lineHeight: 1.6,
  },

  spin: {
    animation: 'spin 1s linear infinite',
  },
};